import React from "react";
import {
  Target,
  Zap,
  Wallet,
  BarChart3,
  Award,
  TrendingUp,
  Users,
  Calendar,
} from "lucide-react";
import PageHero from "../components/PageHero";
import { Section, SectionHeading, Check, Eyebrow } from "../components/SectionPrimitives";
import CTABand from "../components/CTABand";
import { SITE } from "../lib/site";

const benefits = [
  { icon: Target, title: "Clientes com Intenção de Compra", desc: "Você aparece para pessoas que já estão procurando exatamente o que você vende.", color: "#4285F4" },
  { icon: Zap, title: "Resultados Mais Rápidos", desc: "Comece a gerar contatos em poucos dias após a ativação das campanhas.", color: "#EA4335" },
  { icon: Wallet, title: "Controle de Investimento", desc: "Defina exatamente quanto deseja investir e ajuste a qualquer momento.", color: "#FBBC05" },
  { icon: BarChart3, title: "Métricas Reais", desc: "Acompanhe cliques, contatos e conversões em tempo real, com transparência total.", color: "#34A853" },
];

const management = [
  "Pesquisa profunda de palavras-chave",
  "Criação de campanhas estratégicas",
  "Configuração de rastreamento de conversões",
  "Extensões de anúncio (sitelinks, chamadas, local)",
  "Otimizações contínuas e testes A/B",
  "Campanhas de remarketing",
  "Relatórios mensais detalhados",
  "Suporte direto com especialista dedicado",
];

export default function GoogleAds() {
  return (
    <div data-testid="page-google-ads">
      <PageHero
        breadcrumb="Google Ads"
        eyebrow="Performance no Google"
        title="Apareça no topo do Google e gere"
        highlight="mais clientes."
        subtitle="Todos os dias milhares de pessoas pesquisam produtos e serviços no Google. Com Google Ads, sua empresa aparece exatamente no momento em que o cliente procura por você."
      />

      {/* Benefícios */}
      <Section testId="gads-benefits">
        <SectionHeading
          eyebrow="Benefícios do Google Ads"
          title="A plataforma de anúncios mais poderosa do mundo, trabalhando para você."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.title} className="og-card" data-testid={`gads-benefit-${b.title}`}>
                <Icon size={26} style={{ color: b.color }} />
                <h3 className="font-display text-lg font-bold text-[#000000] mt-5">
                  {b.title}
                </h3>
                <p className="mt-3 text-sm text-[#000000]/70 leading-relaxed">
                  {b.desc}
                </p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Nossa Gestão Inclui */}
      <Section testId="gads-management" className="bg-[#F8F9FA] border-y border-gray-100">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow color="#4285F4">Nossa gestão inclui</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#000000] mt-4 leading-[1.05]">
              Gestão profissional, do planejamento ao retorno.
            </h2>
            <p className="mt-6 text-[#000000]/70 leading-relaxed">
              Cada campanha é estruturada por especialistas certificados em
              Google Ads e acompanhada de perto pela nossa equipe estratégica.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-5 bg-white border border-gray-100 p-8">
              {management.map((it, i) => (
                <Check key={it} color={["#4285F4", "#EA4335", "#FBBC05", "#34A853"][i % 4]}>
                  {it}
                </Check>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Por Que Escolher */}
      <Section testId="gads-why-choose">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="bg-[#F8F9FA] border border-gray-100 p-10 flex flex-col items-center text-center">
              <img
                src={SITE.googlePartner}
                alt="Google Partner Badge"
                className="w-44 h-auto"
              />
              <p className="mt-4 eyebrow">Selo Oficial</p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <Eyebrow>Por que escolher a O.G.</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#000000] mt-4 leading-[1.05]">
              <span className="g-underline">Google Partner</span> com track-record real.
            </h2>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                { icon: Award, label: "Google Partner Certificado", color: "#4285F4" },
                { icon: TrendingUp, label: "+R$ 5 Milhões Gerenciados", color: "#EA4335" },
                { icon: Users, label: "+100 Clientes Ativos", color: "#FBBC05" },
                { icon: Calendar, label: "+10 Anos de Experiência", color: "#34A853" },
              ].map((it) => {
                const Icon = it.icon;
                return (
                  <div key={it.label} className="border-t-2 pt-4" style={{ borderColor: it.color }}>
                    <Icon size={20} style={{ color: it.color }} />
                    <p className="mt-2 font-medium text-[#000000]">{it.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Section>

      <CTABand
        title="Solicite uma análise gratuita da sua empresa."
        subtitle="Nosso time avalia sua operação, seu mercado e o potencial de cliques qualificados que podemos gerar para você."
        whatsappMsg="Olá! Quero uma análise gratuita de Google Ads para minha empresa."
      />
    </div>
  );
}
